import React, { useEffect } from 'react';

// Define types
interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: 'primary' | 'secondary';
}

const AboutUs: React.FC = () => {
  // Services data
  const services: Service[] = [
    {
      id: 1,
      title: 'Import Solutions',
      description: 'With extensive experience in global trade, Siddhant Exim Academy excels in import compliance, logistics optimization, and comprehensive support. We also offer Third-Party Inspection, Business Process Outsourcing, and Management services to ensure quality, efficiency, and performance. Trust us to streamline your import processes and enhance operational excellence.',
      icon: 'fas fa-ship',
      color: 'primary'
    },
    {
      id: 2,
      title: 'Buy',
      description: 'Welcome to Siddhant Exim Academy, a division of Ratanlal Traders Intead Pvt. Ltd., specializing in seamless global export. We offer expert consulting, compliance, and logistics solutions to streamline your export processes. Our services include Third-Party Inspection, Business Process Outsourcing, and Call Centre solutions to ensure your success in international markets.',
      icon: 'fas fa-search-dollar',
      color: 'secondary'
    },
    {
      id: 3,
      title: 'Export Services',
      description: 'Welcome to Siddhant Exim Academy, a division of Ratanlal Traders Intead Pvt. Ltd. Specializing in global trade, we offer expert consulting, compliance assurance, and logistics optimization for seamless exports. Our services also include Third-Party Inspection, Business Process Outsourcing, and Call Centre solutions. Trust us for your export success.',
      icon: 'fas fa-plane',
      color: 'secondary'
    },
    {
      id: 4,
      title: 'Sales Growth',
      description: 'At Siddhant Exim Academy, we drive sales success with tailored strategies and expert guidance. Our team excels in optimizing sales processes, enhancing customer engagement, and increasing market reach. We offer comprehensive solutions to boost sales performance, ensuring your business achieves its revenue goals and thrives in a competitive landscape.',
      icon: 'fas fa-chart-line',
      color: 'primary'
    }
  ];

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observe all elements with animation classes
    const animatedElements = document.querySelectorAll(
      '.animate-left, .animate-right, .animate-top, .animate-bottom'
    );
    
    animatedElements.forEach(el => observer.observe(el));

    return () => {
      animatedElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Font Awesome CDN */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInTop {
          from {
            opacity: 0;
            transform: translateY(-100px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInBottom {
          from {
            opacity: 0;
            transform: translateY(100px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-left {
          opacity: 0;
          transition: all 0.8s ease-out;
        }
        
        .animate-right {
          opacity: 0;
          transition: all 0.8s ease-out;
        }
        
        .animate-top {
          opacity: 0;
          transition: all 0.8s ease-out;
        }
        
        .animate-bottom {
          opacity: 0;
          transition: all 0.8s ease-out;
        }
        
        .animate-left.animate-visible {
          animation: slideInLeft 0.8s ease-out forwards;
        }
        
        .animate-right.animate-visible {
          animation: slideInRight 0.8s ease-out forwards;
        }
        
        .animate-top.animate-visible {
          animation: slideInTop 0.8s ease-out forwards;
        }
        
        .animate-bottom.animate-visible {
          animation: slideInBottom 0.8s ease-out forwards;
        }
        
        /* Staggered delays for service items */
        .service-item:nth-child(1) { animation-delay: 0.1s; }
        .service-item:nth-child(2) { animation-delay: 0.2s; }
        .service-item:nth-child(3) { animation-delay: 0.3s; }
        .service-item:nth-child(4) { animation-delay: 0.4s; }
      `}</style>
      
      {/* Header Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-amber-50 py-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-evenly gap-12">
            {/* Text Content - Animate from left */}
            <div className="w-full lg:w-1/2 text-center lg:text-left animate-left">
              <div className="relative mb-8">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-6">
                  About <span className="text-amber-500">Us</span>
                </h1>
                <div className="w-24 h-1 bg-amber-500 rounded-full mx-auto lg:mx-0 mb-6"></div>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                 Involved in export since 2014 and business consultancy since 2018, we operate through a strong global trade network connecting markets worldwide. Based in the UAE, we provide a platform for new entrepreneurs, support participation in trade events, and promote sustainable business growth through trusted international partnerships.
                </p>
              </div>
            </div>
            
            {/* Image - Animate from right */}
            <div className="w-full lg:w-1/3 animate-right">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                  alt="Team Collaboration"
                  className="rounded-[80px] shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-amber-400 rounded-2xl -z-10"></div>
                <div className="absolute -top-6 -right-6 w-20 h-20 bg-teal-400 rounded-2xl -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Heading - Animate from top */}
          <div className="text-center mb-16 animate-top">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              WHAT I <span className="text-amber-500"> DO</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions designed to meet your unique business needs and drive sustainable growth.
            </p>
          </div>
          
          {/* Services Grid with alternating animations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div 
                key={service.id}
                className={`service-item group bg-white rounded-2xl p-8 border border-gray-200 hover:border-teal-300 transition-all duration-300 hover:shadow-xl ${
                  index % 2 === 0 ? 'animate-left' : 'animate-right'
                } ${
                  index % 2 === 1 ? 'hover:transform hover:-translate-y-2' : ''
                }`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                  service.color === 'primary' 
                    ? 'bg-teal-100 text-teal-600' 
                    : 'bg-amber-100 text-amber-600'
                }`}>
                  <i className={`${service.icon} text-xl`}></i>
                </div>
                
                <h3 className={`text-2xl font-bold mb-4 ${
                  service.color === 'primary' ? 'text-teal-700' : 'text-amber-700'
                }`}>
                  {service.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;